<template>
    <div class="bg-white my-2">
        <a :as="hasAgency ? 'a' : Link" prefetch="hover" :href="url"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 overflow-hidden rounded-sm item body-text"
            :class="{ loading: loading }">
            <div v-if="loading" class="w-full" style="aspect-ratio: 4/3">
                <skeleton width="100%" height="100%" />
            </div>
            <div v-else class="relative overflow-hidden">
                <div class="flex items-center justify-center w-full overflow-hidden item-image" style="
                        aspect-ratio: 4/3;
                        background: #fff;
                        min-height: 100%;
                    ">
                    <lazy-image :eager="priority">
                        <template #image="{ load }">
                            <img class="!min-w-[140%] md:!min-w-[130%] md:!min-w-[120%]" width="400" height="300"
                            :src="(
                                tour.images[tour.cover_photo] ??
                                tour.images[0]
                            ).medium
                                " :alt="tour.name" :loading="priority ? 'eager' : 'lazy'"
                                :fetchpriority="priority ? 'high' : undefined" decoding="async" @load.once="load" />
                        </template>
                    </lazy-image>
                </div>

                <div class="absolute top-0 bottom-0 left-0 right-0 flex justify-end items-start p-1 item-overlay">
                    <overlay-default :tour="tour" />
                </div>
                <promotions :tour="tour" />
            </div>

            <div class="flex flex-col gap-2 p-2 col-span-1 lg:col-span-2 h-full justify-between">
                <div class="flex flex-col gap-1" v-if="loading">
                    <skeleton height="2.4em" />
                    <skeleton width="8rem" />
                </div>


                <div v-else>
                    <h4 class="h6 !font-semibold body-text line-clamp-2"
                        style="line-height: 1.2em; min-height: 2.4em">
                        {{ tour.name }}
                    </h4>
                    <rating v-if="tour.average_rating" :rating="tour.average_rating" />
                </div>


                <price v-if="!loading" :tour="tour" size="list" />

                <skeleton v-if="loading" />
                <div v-else-if="tour.inclusions.length > 0" class="flex flex-wrap gap-2 text-xs font-medium">
                    <div>Includes:</div>
                    <div class="flex items-center justify-center gap-1" v-for="tag in tour.inclusions">
                        <span :class="'fa ' + tag.icon" />
                        <span>{{ tag.name }}</span>
                    </div>
                </div>

                <div class="flex justify-end p-2">
                    <p-button v-if="!loading" class="rounded-0" text severity="secondary">
                        <span style="letter-spacing: 0.1em" class="text-xs text-primary uppercase">
                            Explore
                        </span>
                        <span style="font-size: 8px" class="pi pi-chevron-right text-primary"></span>
                    </p-button>
                </div>
            </div>
        </a>
    </div>
</template>

<script setup lang="ts">
import { Tour } from "@/types";
import { Skeleton, Button as PButton } from "primevue";
import LazyImage from "@/Components/LazyImage.vue";
import { Link } from "@inertiajs/vue3";
import OverlayDefault from "../OverlayDefault.vue";
import Promotions from "../Promotions.vue";
import Price from "../Price.vue";
import Rating from "@/Components/Reviews/Rating.vue";
import useAgency from "@/Hook/useAgency";

defineProps<{
    tour: Tour;
    loading?: boolean;
    url?: string;
    // Above-the-fold card: render its image eagerly so it is a valid LCP element.
    priority?: boolean;
}>();


const { hasAgency } = useAgency()
</script>

<style scoped lang="scss">
.item {
    .item-image {
        transform: scale(1);
        transition: 0.25s;
    }

    &.loading {
        cursor: wait !important;
    }

    &:hover {
        transition: 0.1s;
        cursor: pointer;

        .item-overlay {
            background-color: rgba(227, 227, 227, 0.205);
            transition: 0.25s;
        }

        .item-image {
            transform: scale(1.2);
            transition: 0.5s;
        }
    }
}
</style>
